/**
 * Scope Selector Component
 * 
 * Dropdown for selecting a specific product (Product only - no "All Products" option).
 * Used in the dashboard header for the Executive Co-Pilot.
 */
import React from 'react';
import { ChevronDown, Globe, Package, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';

const ScopeSelector = ({ 
  scope, 
  scopeLabel, 
  products, 
  onScopeChange,
  disabled,
  className 
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();

  const handleSelect = (newScope, newLabel) => {
    if (newScope !== scope) {
      onScopeChange(newScope, newLabel);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          className={cn(
            "flex items-center gap-2 min-w-[180px] justify-between",
            colors.bgSecondary, colors.borderPrimary,
            className
          )}
        >
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-purple-500" />
            <span className={cn("text-sm truncate max-w-[120px]", colors.textPrimary)}>
              {scopeLabel || 'Select a Product'}
            </span>
          </div>
          <ChevronDown className={cn("w-4 h-4 ml-1", colors.textMuted)} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {products && products.length > 0 ? (
          <>
            <div className="px-2 py-1.5">
              <span className={cn("text-xs font-medium", colors.textMuted)}>
                Select a Product
              </span>
            </div>
            {products.map((product) => (
              <DropdownMenuItem
                key={product.product_id}
                onClick={() => handleSelect(product.product_id, product.name)}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-purple-500" />
                  <span className="truncate max-w-[160px]">{product.name}</span>
                </div>
                {scope === product.product_id && <Check className="w-4 h-4 text-indigo-600" />}
              </DropdownMenuItem>
            ))}
          </>
        ) : (
          <div className="px-2 py-3 text-center">
            <span className={cn("text-sm", colors.textMuted)}>No products available</span>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ScopeSelector;
